import { z } from "zod";

import { apiRequest } from "./apiUtils";

/* Worker Dashboard v2 API (SPEC handoff-worker §6). First slice: the "For you today"
   attention strip (the confirm nudge) + the confirm action. */

/* ── For you today (SPEC §3.1) ─────────────────────────────────────────────── */

export const WorkerAttentionItemSchema = z.object({
  id: z.string(),
  type: z.enum([
    "offer",
    "confirm_shift",
    "approved",
    "known_place_posted",
    "declined",
    "new_message",
  ]),
  severity: z.enum(["urgent", "normal"]).default("normal"),
  title: z.string().nullable().optional(),
  subtext: z.string().nullable().optional(),
  shift_id: z.number().nullable().optional(),
  primary_action: z
    .object({
      label: z.string().nullable().optional(),
      kind: z.enum(["confirm", "review_offer", "apply", "find_similar", "view_message"]),
      target_id: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});
export type WorkerAttentionItem = z.infer<typeof WorkerAttentionItemSchema>;

const WorkerAttentionResponseSchema = z.object({
  success: z.boolean().default(true),
  items: z.array(WorkerAttentionItemSchema).default([]),
});

export async function getWorkerAttention(): Promise<Array<WorkerAttentionItem>> {
  const data = await apiRequest<unknown>("worker/dashboard/attention", { method: "GET" });
  return WorkerAttentionResponseSchema.parse(data).items;
}

/* ── Your upcoming shifts (SPEC §3.2) ──────────────────────────────────────── */

export const WorkerUpcomingSchema = z.object({
  shift_id: z.number(),
  restaurant: z
    .object({
      id: z.number().nullable().optional(),
      name: z.string().nullable().optional(),
      address: z.string().nullable().optional(),
      lat: z.number().nullable().optional(),
      lng: z.number().nullable().optional(),
    })
    .nullable()
    .optional(),
  role: z.string().nullable().optional(),
  start_at: z.string().nullable().optional(),
  end_at: z.string().nullable().optional(),
  rate: z.coerce.number().nullable().catch(null),
  estimated_pay: z.coerce.number().nullable().catch(null),
  status: z.enum(["offered", "applied", "approved", "confirmed"]).catch("approved"),
  confirmed_at: z.string().nullable().optional(),
  checkin_opens_at: z.string().nullable().optional(),
  distance_mi: z.coerce.number().nullable().catch(null),
});
export type WorkerUpcomingShift = z.infer<typeof WorkerUpcomingSchema>;

const WorkerUpcomingResponseSchema = z.object({
  success: z.boolean().default(true),
  items: z.array(WorkerUpcomingSchema).default([]),
  /** All upcoming shifts before the limit (older backends omit it → fall back to items.length). */
  total: z.coerce.number().nullable().optional(),
});
export type WorkerUpcomingResult = { items: Array<WorkerUpcomingShift>; total: number };

export async function getWorkerUpcoming(limit = 3): Promise<WorkerUpcomingResult> {
  const data = await apiRequest<unknown>(`worker/dashboard/upcoming?limit=${limit}`, {
    method: "GET",
  });
  const parsed = WorkerUpcomingResponseSchema.parse(data);
  return { items: parsed.items, total: parsed.total ?? parsed.items.length };
}

/* ── Confirm / withdraw (SPEC §3.1/§3.2) ───────────────────────────────────── */

export async function confirmShift(shiftId: number | string): Promise<void> {
  await apiRequest<unknown>(`worker/shift/${shiftId}/confirm`, { method: "POST" });
}

export async function withdrawFromShift(shiftId: number | string): Promise<void> {
  await apiRequest<unknown>("worker/shift/add-update-offer", {
    method: "POST",
    body: JSON.stringify({ shift_id: shiftId, interest_status: "WITHDRAWN" }),
  });
}

/** Apply to / express interest in a shift. Matches the existing worker apply flow. */
export async function applyToShift(shiftId: number | string): Promise<void> {
  await apiRequest<unknown>("worker/shift/add-update-offer", {
    method: "POST",
    body: JSON.stringify({ shift_id: shiftId, interest_status: "ACCEPTED" }),
  });
}

/** Accept a shift an employer offered (first to accept gets it — 409 if already filled). */
export async function acceptOffer(shiftId: number | string): Promise<void> {
  await applyToShift(shiftId);
}

/** Decline a shift an employer offered. */
export async function declineOffer(shiftId: number | string): Promise<void> {
  await apiRequest<unknown>("worker/shift/add-update-offer", {
    method: "POST",
    body: JSON.stringify({ shift_id: shiftId, interest_status: "REJECTED" }),
  });
}

/* ── Change / cancel an upcoming shift (SPEC §3.2) ─────────────────────────── */

export const CANCEL_REASONS = [
  "ILLNESS",
  "EMERGENCY",
  "TRANSPORTATION",
  "SCHEDULE_CONFLICT",
  "FOUND_OTHER_WORK",
  "NO_LONGER_AVAILABLE",
  "OTHER",
] as const;
export type CancelReason = (typeof CANCEL_REASONS)[number];

/** Cancel an upcoming shift the worker can no longer make. */
export async function cancelShift(
  shiftId: number | string,
  { reason, note }: { reason: CancelReason; note?: string },
): Promise<void> {
  await apiRequest<unknown>(`worker/shift/${shiftId}/cancel`, {
    method: "POST",
    body: JSON.stringify({ reason, note }),
  });
}

/** Let the employer know the worker is running late for an upcoming shift. */
export async function reportRunningLate(
  shiftId: number | string,
  { etaMinutes, note }: { etaMinutes?: number; note?: string },
): Promise<void> {
  await apiRequest<unknown>(`worker/shift/${shiftId}/running-late`, {
    method: "POST",
    body: JSON.stringify({ eta_minutes: etaMinutes, note }),
  });
}

/* ── Available near you (SPEC §3.3) ────────────────────────────────────────── */

export const WorkerNearbySchema = z.object({
  shift_id: z.number(),
  restaurant: z
    .object({
      id: z.number().nullable().optional(),
      name: z.string().nullable().optional(),
      lat: z.number().nullable().optional(),
      lng: z.number().nullable().optional(),
    })
    .nullable()
    .optional(),
  role: z.string().nullable().optional(),
  start_at: z.string().nullable().optional(),
  end_at: z.string().nullable().optional(),
  rate: z.coerce.number().nullable().catch(null),
  estimated_pay: z.coerce.number().nullable().catch(null),
  distance_mi: z.coerce.number().nullable().catch(null),
  worked_count: z.coerce.number().catch(0),
  posted_at: z.string().nullable().optional(),
  applied: z.boolean().catch(false),
});
export type WorkerNearbyShift = z.infer<typeof WorkerNearbySchema>;

const WorkerNearbyResponseSchema = z.object({
  success: z.boolean().default(true),
  total: z.coerce.number().catch(0),
  mine_count: z.coerce.number().catch(0),
  items: z.array(WorkerNearbySchema).default([]),
  days_with_openings: z.array(z.string()).default([]),
  next_cursor: z.string().nullable().optional(),
});
export type WorkerNearbyResult = z.infer<typeof WorkerNearbyResponseSchema>;

export type NearbyParams = {
  start?: string;
  end?: string;
  radiusMi?: number;
  places?: "all" | "mine";
  limit?: number;
  cursor?: string;
  lat?: number;
  lng?: number;
};

export async function getWorkerNearby(p: NearbyParams): Promise<WorkerNearbyResult> {
  const q = new URLSearchParams();
  if (p.start) q.set("start", p.start);
  if (p.end) q.set("end", p.end);
  if (p.radiusMi != null) q.set("radius_mi", String(p.radiusMi));
  if (p.places) q.set("places", p.places);
  if (p.limit != null) q.set("limit", String(p.limit));
  if (p.cursor) q.set("cursor", p.cursor);
  if (p.lat != null) q.set("lat", String(p.lat));
  if (p.lng != null) q.set("lng", String(p.lng));
  const data = await apiRequest<unknown>(`worker/dashboard/nearby?${q.toString()}`, { method: "GET" });
  return WorkerNearbyResponseSchema.parse(data);
}

/* ── Activity (SPEC §3.4) ──────────────────────────────────────────────────── */

export const WorkerEventFilters = ["all", "applied", "approved", "completed"] as const;
export type WorkerEventFilter = (typeof WorkerEventFilters)[number];

export const WorkerEventSchema = z.object({
  id: z.string(),
  type: z
    .enum([
      "offered",
      "approved",
      "confirmed",
      "known_place_posted",
      "reminder",
      "nearby_posted",
      "applied",
      "paid",
      "completed",
      "declined",
    ])
    .catch("applied"),
  occurred_at: z.string().nullable().optional(),
  actor: z.object({ name: z.string().nullable().optional() }).nullable().optional(),
  shift: z
    .object({
      id: z.number().nullable().optional(),
      role_name: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  note: z.string().nullable().optional(),
});
export type WorkerEvent = z.infer<typeof WorkerEventSchema>;

const WorkerEventsResponseSchema = z.object({
  success: z.boolean().default(true),
  unread_count: z.coerce.number().catch(0),
  items: z.array(WorkerEventSchema).default([]),
  next_cursor: z.string().nullable().optional(),
});

export async function getWorkerEvents(
  filter: WorkerEventFilter = "all",
  limit = 7,
): Promise<{ unread: number; items: Array<WorkerEvent> }> {
  const data = await apiRequest<unknown>(
    `worker/dashboard/events?filter=${filter}&limit=${limit}`,
    { method: "GET" },
  );
  const p = WorkerEventsResponseSchema.parse(data);
  return { unread: p.unread_count, items: p.items };
}

export async function markWorkerEventsRead(): Promise<void> {
  await apiRequest<unknown>("worker/dashboard/events/mark-read", { method: "POST" });
}
